import * as yup from "yup";
import { v4 as uuidv4 } from "uuid";
import { Box, BoxProps, Flex } from "@chakra-ui/react";
import { ReactNode, useMemo } from "react";
import { FieldValues, Path } from "react-hook-form";
import { AddressPatch } from "__generated__/gql/graphql";
import { Writeable } from "utils/typescriptUtilis";
import { gql } from "__generated__/gql";
import StateSelect from "components/Shared/Forms/StateSelect";
import * as addressSchemaFields from "utils/schemas/address";
import createFormFieldComponents from "./FormField";

export const updateOrCreateAddressFragment = gql(/* GraphQL */ `
  fragment UpdateOrCreateAddress on Mutation {
    updateOrCreateAddress(
      input: { addressId: $addressId, addressData: $addressData }
    ) @include(if: $isAddressUpdatedOrCreated) {
      address {
        id
        ...AddressComponentFields
      }
    }
  }
`);

export const addressFields = [
  "line1",
  "line2",
  "city",
  "state",
  "zip",
] as const;

export type AddressFieldsetType = Writeable<
  Pick<AddressPatch, (typeof addressFields)[number]>
>;

type AddressFields = Path<AddressFieldsetType>;

export function getHasAddressData(data: AddressFieldsetType) {
  return addressFields.some((f) => data[f]);
}

export type AllowableFieldValues<
  TFieldValues extends FieldValues,
  TFieldName extends Path<TFieldValues> = Path<TFieldValues>,
> = TFieldName extends `${infer TPrefix}.${AddressFields}` ? TPrefix : never;

export const addressFieldsetSchema: yup.ObjectSchema<AddressFieldsetType> = yup
  .object()
  .shape(addressSchemaFields);

type UpdateOrCreateAddressVariables = {
  addressData?: AddressPatch;
  addressId: UUID | null;
  isAddressUpdatedOrCreated: boolean;
};

export function getUpdateOrCreateVariables(
  addressId: UUID | null | undefined,
  formData: AddressFieldsetType | null,
): UpdateOrCreateAddressVariables {
  if (!formData) {
    return { addressId: null, isAddressUpdatedOrCreated: false };
  }
  const hasAddressData = getHasAddressData(formData);
  if (!hasAddressData) {
    return { addressId: null, isAddressUpdatedOrCreated: false };
  }
  return {
    addressData: formData,
    addressId: addressId || uuidv4(),
    isAddressUpdatedOrCreated: true,
  };
}

type Props<
  TFieldValues extends FieldValues,
  TFieldNamePrefix extends Extract<
    Path<TFieldValues>,
    AllowableFieldValues<TFieldValues>
  >,
> = BoxProps & {
  fieldNamePrefix: TFieldNamePrefix;
  legend?: ReactNode;
  legendLabel?: ReactNode;
};

export default function AddressFieldset<
  TFieldValues extends FieldValues,
  TFieldNamePrefix extends Extract<
    Path<TFieldValues>,
    AllowableFieldValues<TFieldValues>
  > = Extract<Path<TFieldValues>, AllowableFieldValues<TFieldValues>>,
>({
  fieldNamePrefix,
  legend,
  legendLabel,
  ...rest
}: Props<TFieldValues, TFieldNamePrefix>) {
  const { FormField, ControlledFormField } = useMemo(() => {
    const schema = fieldNamePrefix
      .split(".")
      .reverse()
      .reduce<yup.AnySchema>(
        (acc, k, i) =>
          yup.object().shape({ [k]: i === 0 ? addressFieldsetSchema : acc }),
        yup.object(),
      ) as unknown as yup.ObjectSchema<TFieldValues>;

    return createFormFieldComponents<TFieldValues>({ schema });
  }, [fieldNamePrefix]);

  function getPrefixedFieldName(name: AddressFields) {
    return `${fieldNamePrefix}.${name}` as Path<TFieldValues>;
  }

  return (
    <Box as="fieldset" {...rest}>
      {legend ||
        (legendLabel && (
          <Box
            as="legend"
            fontFamily="heading"
            color="magenta.veryDark"
            borderBottomWidth={1}
            display="block"
            w="100%"
          >
            {legendLabel}
          </Box>
        ))}

      <FormField name={getPrefixedFieldName("line1")} />
      <FormField name={getPrefixedFieldName("line2")} />

      <Flex
        mx={-1}
        my={-1.5}
        flexWrap="wrap"
        // sx={{ ">*": { minW: "100% !important" } }}
      >
        <FormField
          name={getPrefixedFieldName("city")}
          mx={1}
          my={3 / 2}
          flex="42 0 9em"
          minW={0}
        />
        <ControlledFormField
          name={getPrefixedFieldName("state")}
          renderInput={({ field }) => (
            <StateSelect {...field} placeholder="-----" />
          )}
          mx={1}
          my={3 / 2}
          flex="36 0 8em"
          minW={0}
        />
        <FormField
          name={getPrefixedFieldName("zip")}
          mx={1}
          my={3 / 2}
          flex="22 0 5.5em"
          minW={0}
        />
      </Flex>
    </Box>
  );
}
